import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { WifiOff } from 'lucide-react';
import { cn } from '@xdocs/ui';
import { useWebT } from '../i18n/useWebT.js';
import { useWs } from '../ws/WsProvider.js';
import styles from './ConnectionBanner.module.css';

const BACK_ONLINE_VISIBLE_MS = 4000;

/**
 * E19/E20 §0.1 connection-status vocabulary — `reconnecting…` / `back
 * online` / offline — the ONLY names used anywhere in the product for this
 * (a fourth synonym is a review block). Global banner (ux.md: app-level
 * persistent condition), `role="status"` (non-blocking, not `alert`).
 */
export function ConnectionBanner(): ReactElement | null {
  const { t } = useWebT();
  const { wsClient, connectionState } = useWs();
  const [showBackOnline, setShowBackOnline] = useState(false);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const off = wsClient.on('back-online', () => {
      setShowBackOnline(true);
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
      dismissTimer.current = setTimeout(() => setShowBackOnline(false), BACK_ONLINE_VISIBLE_MS);
    });
    return () => {
      off();
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
    };
  }, [wsClient]);

  if (connectionState === 'reconnecting' || connectionState === 'connecting') {
    return (
      <div className={cn(styles['banner'])} data-tone="warning" role="status" aria-live="polite">
        <span className={cn(styles['dot'])} aria-hidden="true" />
        {t('web:connection.reconnecting')}
      </div>
    );
  }

  if (connectionState === 'offline') {
    return (
      <div className={cn(styles['banner'])} data-tone="warning" role="status" aria-live="polite">
        <WifiOff size={14} aria-hidden="true" />
        {t('web:connection.offline')}
      </div>
    );
  }

  if (showBackOnline && connectionState === 'connected') {
    return (
      <div className={cn(styles['banner'])} data-tone="success" role="status" aria-live="polite">
        {t('web:connection.backOnline')}
      </div>
    );
  }

  return null;
}
